import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, RefreshCw, Truck, Phone, Mail, Building2, MapPin,
  CheckCircle2, AlertTriangle, X, ChevronRight, Calendar,
  Package, Droplets, Wrench, Send, FileText,
} from "lucide-react";
import SourceFromContractorsModal from "@/components/admin/SourceFromContractorsModal";
import DIPoDispatchDialog from "@/components/admin/DIPoDispatchDialog";

const API = process.env.REACT_APP_BACKEND_URL;

// Status pipeline + visual config. Each status owns a color and the next
// transition button label, so the row UI stays declarative.
const STATUSES = [
  { id: "new",              label: "New",              color: "bg-amber-100 text-amber-800 border-amber-200",   nextLabel: "Mark Confirmed",        next: "confirmed" },
  { id: "confirmed",        label: "Confirmed",        color: "bg-sky-100 text-sky-800 border-sky-200",         nextLabel: "Mark Out for Delivery", next: "out_for_delivery" },
  { id: "out_for_delivery", label: "Out for Delivery", color: "bg-violet-100 text-violet-800 border-violet-200",nextLabel: "Mark Completed",        next: "completed" },
  { id: "completed",        label: "Completed",        color: "bg-emerald-100 text-emerald-800 border-emerald-200", nextLabel: null,                next: null },
  { id: "cancelled",        label: "Cancelled",        color: "bg-slate-100 text-slate-600 border-slate-200",   nextLabel: null,                    next: null },
];
const STATUS_BY_ID = Object.fromEntries(STATUSES.map((s) => [s.id, s]));

function StatusPill({ status }) {
  const s = STATUS_BY_ID[status] || STATUS_BY_ID.new;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${s.color}`}
      data-testid={`di-status-pill-${status}`}
    >
      {s.label}
    </span>
  );
}

function UrgencyBadge({ urgency }) {
  if (urgency === "same_day_emergency") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
        <AlertTriangle className="w-3 h-3" /> Same-day
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-cyan-50 text-cyan-700 border border-cyan-200">
      Next-day
    </span>
  );
}

export default function AdminDITankExchange({ token, onQuoteOrder, linkingId }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("open");
  const [filterUrgency, setFilterUrgency] = useState("all");
  const [selected, setSelected] = useState(null);
  const [sourceFor, setSourceFor] = useState(null);  // request being dispatched
  const [poFor, setPoFor] = useState(null);          // request being PO'd

  const authHeader = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterUrgency !== "all") params.urgency = filterUrgency;
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/di-tank-exchange/requests`, { headers: authHeader, params }),
        axios.get(`${API}/api/admin/di-tank-exchange/stats`, { headers: authHeader }),
      ]);
      setItems(listRes.data?.items || []);
      setStats(statsRes.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load DI tank exchange requests");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [filterStatus, filterUrgency]);

  const advanceStatus = async (req, nextStatus, note = "") => {
    try {
      const r = await axios.patch(
        `${API}/api/admin/di-tank-exchange/requests/${req.id}/status`,
        { status: nextStatus, note: note || undefined },
        { headers: authHeader },
      );
      toast.success(`Marked ${STATUS_BY_ID[nextStatus]?.label || nextStatus}`);
      setItems((arr) => arr.map((it) => (it.id === req.id ? r.data.request : it)));
      if (selected?.id === req.id) setSelected(r.data.request);
      // Refresh stats but don't re-fetch list since we already patched in place
      const sRes = await axios.get(`${API}/api/admin/di-tank-exchange/stats`, { headers: authHeader });
      setStats(sRes.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-di-tank-exchange">
      {/* Header + refresh */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center">
            <Droplets className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">DI Tank Exchange Requests</h2>
            <p className="text-sm text-slate-500">Inbound exchange orders from <span className="font-mono text-xs">/solutions/water-treatment</span>. Track dispatch from new → completed.</p>
          </div>
        </div>
        <Button onClick={fetchAll} variant="outline" className="gap-1.5" data-testid="di-admin-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* KPI strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="di-admin-kpis">
          {[
            { label: "Open", value: stats.open, sub: `${stats.total} all-time`, color: "amber" },
            { label: "New", value: stats.status_counts?.new || 0, sub: "awaiting confirmation", color: "amber" },
            { label: "Confirmed", value: stats.status_counts?.confirmed || 0, sub: "scheduled", color: "sky" },
            { label: "Out for Delivery", value: stats.status_counts?.out_for_delivery || 0, sub: "in transit", color: "violet" },
            { label: "Completed", value: stats.status_counts?.completed || 0, sub: `${stats.total_tanks_requested || 0} tanks total`, color: "emerald" },
            { label: "Avg / request", value: stats.avg_tanks_per_request || 0, sub: `${stats.urgency_counts?.same_day_emergency || 0} same-day · ${stats.urgency_counts?.next_day || 0} next-day`, color: "slate" },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5" data-testid={`di-kpi-${kpi.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{kpi.label}</p>
              <p className={`text-2xl font-bold mt-0.5 text-${kpi.color}-700`}>{kpi.value}</p>
              <p className="text-[10px] text-slate-400 truncate" title={kpi.sub}>{kpi.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 border border-slate-200 rounded-lg bg-slate-50/50 px-3 py-2" data-testid="di-admin-filters">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Status:</span>
        {[
          { id: "open", label: "Open" },
          { id: "all", label: "All" },
          ...STATUSES,
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => setFilterStatus(opt.id)}
            className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
              filterStatus === opt.id
                ? "bg-cyan-600 text-white border-cyan-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-cyan-300"
            }`}
            data-testid={`di-filter-status-${opt.id}`}
          >
            {opt.label}
          </button>
        ))}
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-3">Urgency:</span>
        {[
          { id: "all", label: "All" },
          { id: "next_day", label: "Next-day" },
          { id: "same_day_emergency", label: "Same-day" },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => setFilterUrgency(opt.id)}
            className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
              filterUrgency === opt.id
                ? "bg-rose-600 text-white border-rose-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-rose-300"
            }`}
            data-testid={`di-filter-urgency-${opt.id}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Truck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="font-bold text-slate-700">No DI tank exchange requests {filterStatus === "open" ? "currently open" : "match the filter"}.</p>
            <p className="text-sm text-slate-500 mt-1">Customers can place an order at <span className="font-mono text-xs">/solutions/water-treatment#di-tank-exchange</span>.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Company</th>
                <th className="text-left px-4 py-2.5">Contact</th>
                <th className="text-center px-4 py-2.5">Tanks</th>
                <th className="text-left px-4 py-2.5">Tank Size</th>
                <th className="text-left px-4 py-2.5">Submitted</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it) => {
                const cur = STATUS_BY_ID[it.status] || STATUS_BY_ID.new;
                return (
                  <tr key={it.id} className="hover:bg-slate-50/60 cursor-pointer" data-testid={`di-row-${it.id}`} onClick={() => setSelected(it)}>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-col gap-1">
                        <StatusPill status={it.status} />
                        <UrgencyBadge urgency={it.urgency} />
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-slate-900">{it.company_name}</p>
                      {it.account_number && <p className="text-[11px] text-slate-500 font-mono">{it.account_number}</p>}
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="text-slate-900">{it.full_name}</p>
                      <p className="text-[11px] text-slate-500">{it.email}</p>
                      <p className="text-[11px] text-slate-500">{it.phone}</p>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-900 font-bold text-sm">
                        {it.number_of_tanks}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{it.tank_size || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">
                      {it.created_at ? format(new Date(it.created_at), "MMM d, yyyy h:mma") : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {onQuoteOrder && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onQuoteOrder("public_order", it.id); }}
                            disabled={linkingId === it.id}
                            className="inline-flex items-center gap-1 text-xs font-bold bg-white border border-cyan-300 text-cyan-700 hover:bg-cyan-50 px-2.5 py-1 rounded-md disabled:opacity-60"
                            title={it.sourcing_request_id
                              ? `Open sourcing request ${it.sourcing_request_id}`
                              : "Open in the Sourcing Desk to quote, order, ship and invoice"}
                            data-testid={`di-quote-order-${it.id}`}
                          >
                            <FileText className="w-3 h-3" /> {it.sourcing_request_id ? "Open Order" : "Quote / Order"}
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setSourceFor(it); }}
                          className="inline-flex items-center gap-1 text-xs font-bold bg-white border border-cyan-300 text-cyan-700 hover:bg-cyan-50 px-2.5 py-1 rounded-md"
                          title="Dispatch via Source from Contractors"
                          data-testid={`di-source-${it.id}`}
                        >
                          <Wrench className="w-3 h-3" /> Source
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setPoFor(it); }}
                          className="inline-flex items-center gap-1 text-xs font-bold bg-white border border-cyan-300 text-cyan-700 hover:bg-cyan-50 px-2.5 py-1 rounded-md"
                          title="Send a Sterile Mate PO to contracted suppliers/contractors"
                          data-testid={`di-send-po-${it.id}`}
                        >
                          <FileText className="w-3 h-3" /> Send PO
                        </button>
                        {cur.next && (
                          <button
                            onClick={(e) => { e.stopPropagation(); advanceStatus(it, cur.next); }}
                            className="inline-flex items-center gap-1 text-xs font-bold bg-cyan-600 hover:bg-cyan-700 text-white px-2.5 py-1 rounded-md"
                            data-testid={`di-advance-${it.id}`}
                          >
                            {cur.nextLabel} <ChevronRight className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <DetailDrawer
          req={selected}
          onClose={() => setSelected(null)}
          onAdvance={(nextStatus, note) => advanceStatus(selected, nextStatus, note)}
          onSource={() => setSourceFor(selected)}
          onSendPo={() => setPoFor(selected)}
        />
      )}

      {/* Source-from-Contractors dispatch modal */}
      {sourceFor && (
        <SourceFromContractorsModal
          requestId={sourceFor.id}
          token={token}
          onClose={() => setSourceFor(null)}
          onBroadcast={() => { fetchAll(); }}
        />
      )}

      {/* Send-PO dialog — one PO per vendor, drafted in Outlook */}
      {poFor && (
        <DIPoDispatchDialog
          diRequestId={poFor.id}
          token={token}
          onClose={() => setPoFor(null)}
          onSent={() => { fetchAll(); }}
        />
      )}
    </div>
  );
}

function DetailDrawer({ req, onClose, onAdvance, onSource, onSendPo }) {
  const [note, setNote] = useState("");
  const cur = STATUS_BY_ID[req.status] || STATUS_BY_ID.new;
  const history = req.status_history || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="di-detail-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-gradient-to-br from-cyan-700 to-slate-900 text-white px-6 py-4 flex items-start justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-cyan-200">DI Tank Exchange</p>
            <h3 className="text-lg font-bold">{req.company_name}</h3>
            <div className="flex items-center gap-2 mt-1.5">
              <StatusPill status={req.status} />
              <UrgencyBadge urgency={req.urgency} />
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="di-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Contact */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /><span>{req.full_name}</span></div>
            <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><a href={`mailto:${req.email}`} className="text-cyan-700 hover:underline">{req.email}</a></div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /><a href={`tel:${req.phone}`} className="text-cyan-700 hover:underline">{req.phone}</a></div>
            {req.account_number && (
              <div className="flex items-center gap-2 text-slate-500"><span className="text-[10px] font-bold uppercase tracking-wider">Account #</span><span className="font-mono">{req.account_number}</span></div>
            )}
          </div>

          {/* Order details */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Tanks</p>
              <p className="text-2xl font-bold text-slate-900 flex items-center gap-2">{req.number_of_tanks} <Package className="w-4 h-4 text-slate-400" /></p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Tank size / type</p>
              <p className="text-sm font-semibold text-slate-900">{req.tank_size || "—"}</p>
            </div>
            {req.delivery_address && (
              <div className="col-span-2 bg-white border border-slate-200 rounded-lg p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Delivery address</p>
                <p className="text-sm text-slate-900 whitespace-pre-wrap">{req.delivery_address}</p>
              </div>
            )}
            {req.instructions && (
              <div className="col-span-2 bg-white border border-slate-200 rounded-lg p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Instructions</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{req.instructions}</p>
              </div>
            )}
          </div>

          {/* Primary action — dispatch to contractors via Source-from-Contractors */}
          <div className="border border-cyan-300 bg-gradient-to-br from-cyan-50 to-white rounded-lg p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-700 mb-1.5">Dispatch</p>
            <p className="text-sm text-slate-700 mb-3">Send a BCC'd RFQ to qualified DI tank contractors. Outlook will open with the dispatch scope pre-filled.</p>
            <Button
              onClick={() => onSource?.()}
              className="bg-cyan-600 hover:bg-cyan-700 gap-1.5 w-full"
              data-testid="di-detail-source"
            >
              <Send className="w-4 h-4" /> Source from Contractors
            </Button>
            <Button
              onClick={() => onSendPo?.()}
              variant="outline"
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 gap-1.5 w-full mt-2"
              data-testid="di-detail-send-po"
            >
              <FileText className="w-4 h-4" /> Send PO to Vendors
            </Button>
          </div>

          {/* Status workflow */}
          {cur.next && (
            <div className="border border-cyan-200 bg-cyan-50/40 rounded-lg p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-700 mb-2">Next action</p>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note for the audit log (e.g. delivery window, driver name, dock entry)"
                rows={2}
                className="mb-3 text-sm bg-white"
                data-testid="di-detail-note"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => { onAdvance(cur.next, note); setNote(""); }}
                  className="bg-cyan-600 hover:bg-cyan-700 gap-1.5 text-sm"
                  data-testid="di-detail-advance"
                >
                  <CheckCircle2 className="w-4 h-4" /> {cur.nextLabel}
                </Button>
                {req.status !== "cancelled" && (
                  <Button
                    onClick={() => { onAdvance("cancelled", note); setNote(""); }}
                    variant="outline"
                    className="text-rose-700 border-rose-200 hover:bg-rose-50 text-sm"
                    data-testid="di-detail-cancel"
                  >
                    Cancel request
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Status history</p>
              <ol className="space-y-2">
                {history.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="w-2 h-2 rounded-full bg-cyan-500 mt-2 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2"><StatusPill status={h.status} /><span className="text-[11px] text-slate-500">{h.at ? format(new Date(h.at), "MMM d, h:mma") : ""}</span></div>
                      {h.note && <p className="text-xs text-slate-600 mt-0.5">{h.note}</p>}
                      {h.actor_email && <p className="text-[10px] text-slate-400 mt-0.5">by {h.actor_email}</p>}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <p className="text-[10px] text-slate-400 pt-3 border-t border-slate-100">
            Submitted {req.created_at ? format(new Date(req.created_at), "PPpp") : "—"} · source: <span className="font-mono">{req.source || "—"}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
